import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAction } from "convex/react";
import { Info } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  stripeTerminalListLocations,
  stripeTerminalRegisterReader,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Spinner } from "~/components/ui/spinner";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";
import type { StripeTerminalLocation } from "~/lib/stripe-terminal";

interface RegisterReaderFormState {
  registrationCode: string;
  stripeTerminalLocationId: string;
  label: string;
}

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/terminals/register/$locationId"
)({
  component: RegisterReaderModal,
});

function RegisterReaderModal() {
  const navigate = useNavigate();
  const { locationId } = useParams({
    from: "/_authenticated/settings/terminals/register/$locationId",
  });

  const registerReader = useAction(stripeTerminalRegisterReader);
  const listLocations = useAction(stripeTerminalListLocations);

  const [terminalLocations, setTerminalLocations] = useState<
    Pick<StripeTerminalLocation, "id" | "displayName">[]
  >([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [formData, setFormData] = useState<RegisterReaderFormState>({
    registrationCode: "",
    stripeTerminalLocationId: locationId,
    label: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateFormField = <TKey extends keyof RegisterReaderFormState>(
    key: TKey,
    value: RegisterReaderFormState[TKey]
  ) => {
    setFormData((currentFormData) => ({
      ...currentFormData,
      [key]: value,
    }));
  };

  const loadLocations = useCallback(async () => {
    try {
      setIsLoadingLocations(true);
      const locationsData = await listLocations({});
      const filteredTerminalLocations = locationId
        ? locationsData.filter(
            (
              terminalLocation: Pick<
                StripeTerminalLocation,
                "id" | "displayName"
              >
            ) => terminalLocation.id === locationId
          )
        : locationsData;
      setTerminalLocations(filteredTerminalLocations);

      const firstTerminalLocation = filteredTerminalLocations[0];
      if (filteredTerminalLocations.length === 1 && firstTerminalLocation) {
        setFormData((currentFormData) => ({
          ...currentFormData,
          stripeTerminalLocationId: firstTerminalLocation.id,
        }));
      }
    } catch (error) {
      logger.exception("locations_load_failed", error, { locationId });
      toast.error("Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  }, [listLocations, locationId]);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const handleClose = () => {
    void navigate({ to: "/settings/terminals" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await registerReader({
        registrationCode: formData.registrationCode,
        locationId: formData.stripeTerminalLocationId,
        label: formData.label || undefined,
      });
      ProductEvents.readerRegistered(formData.stripeTerminalLocationId);
      toast.success("Reader registered successfully");
      handleClose();
    } catch (error) {
      logger.exception("reader_registration_failed", error, {
        locationId: formData.stripeTerminalLocationId,
      });
      toast.error(
        error instanceof Error ? error.message : "Failed to register reader"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Register Stripe S700 Reader</DialogTitle>
          <DialogDescription>
            Register an S700 countertop reader to an existing Stripe Terminal
            Location using the code shown on the device.
          </DialogDescription>
        </DialogHeader>
        {isLoadingLocations ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="size-8" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Info callout about M2 readers */}
              <div className="border-info/30 bg-info/10 dark:border-info/30 dark:bg-info/10 flex gap-3 rounded-lg border p-3">
                <Info className="text-info dark:text-info mt-0.5 size-5 shrink-0" />
                <div className="text-sm">
                  <p className="text-info dark:text-info/80 font-medium">
                    Have an M2 reader?
                  </p>
                  <p className="text-info dark:text-info/70 mt-0.5">
                    M2 mobile readers connect via Bluetooth and are set up
                    directly in the POS app. This registration is only for S700
                    countertop readers.
                  </p>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="registrationCode">Registration Code</Label>
                <Input
                  id="registrationCode"
                  onChange={(e) => {
                    updateFormField("registrationCode", e.target.value);
                  }}
                  placeholder="Enter code from S700 reader"
                  required
                  value={formData.registrationCode}
                />
                <p className="text-muted-foreground text-sm">
                  Find this code on the S700 reader screen.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="stripeTerminalLocationId">
                  Stripe Terminal Location
                </Label>
                <Select
                  onValueChange={(value) => {
                    updateFormField("stripeTerminalLocationId", value);
                  }}
                  required
                  value={formData.stripeTerminalLocationId}
                >
                  <SelectTrigger id="stripeTerminalLocationId">
                    <SelectValue placeholder="Select a Stripe Terminal Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {terminalLocations.map((terminalLocation) => (
                      <SelectItem
                        key={terminalLocation.id}
                        value={terminalLocation.id}
                      >
                        {terminalLocation.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="label">Reader Label (Optional)</Label>
                <Input
                  id="label"
                  onChange={(e) => {
                    updateFormField("label", e.target.value);
                  }}
                  placeholder="e.g., Front Counter"
                  value={formData.label}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                disabled={isSubmitting}
                onClick={handleClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Registering...
                  </>
                ) : (
                  "Register Reader"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
